// First-touch acquisition attribution (UTM params + external referrer).
// Captured once on app load and attached to the onboarding_complete event so
// GA4 can break new tenants down by acquisition channel. Best-effort: any
// storage/parse failure silently yields no attribution.

const STORAGE_KEY = 'prospero_attribution';
const MAX_VALUE_LENGTH = 200;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

export type Attribution = Partial<Record<(typeof UTM_KEYS)[number] | 'referrer', string>>;

export function computeAttribution(input: {
  search: string;
  referrer: string;
  origin: string;
}): Attribution | null {
  const params = new URLSearchParams(input.search);
  const attribution: Attribution = {};

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) {
      attribution[key] = value.slice(0, MAX_VALUE_LENGTH);
    }
  }

  // Only keep an external referrer — same-origin navigations aren't a channel.
  if (input.referrer && !input.referrer.startsWith(input.origin)) {
    attribution.referrer = input.referrer.slice(0, MAX_VALUE_LENGTH);
  }

  return Object.keys(attribution).length > 0 ? attribution : null;
}

export function captureAttribution(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    // First-touch: never overwrite an existing capture. A visit with no
    // attribution stores nothing, so a later attributed visit can still win.
    if (localStorage.getItem(STORAGE_KEY)) {
      return;
    }
    const attribution = computeAttribution({
      search: window.location.search,
      referrer: document.referrer,
      origin: window.location.origin,
    });
    if (attribution) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    }
  } catch {
    // localStorage unavailable / quota — attribution is best-effort.
  }
}

export function getAttribution(): Attribution {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : {};
  } catch {
    return {};
  }
}
